// v2 elephant — a full savanna bull: pillar legs ringed with pale toenail
// bands, a massive barrel between a shoulder hump and a heavy rump, a domed
// head flanked by huge two-layer fan ears, a four-segment trunk curling down to
// a lifted tip, long two-segment ivory tusks and a rope tail with a dark tuft.
// Two coats: slate grey and red-dust brown (dust-bathed). Ported 1:1 from
// age-of-mcp's v2/elephant.ts, using the library's shared `critter` (quadLegs)
// and `safari` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { hide: "#8e8e94", shade: "#80808a", leg: "#85858c", earIn: "#ab8d8c",
    tusk: "#e9e2cc", nail: "#d9d2bf", tuft: "#46413c" },
  { hide: "#9a8a7a", shade: "#8a7a6c", leg: "#90806f", earIn: "#b3978a",
    tusk: "#ece4cf", nail: "#ddd4be", tuft: "#4c4238" },
];

function elephantParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.13, dz: 0.08, bodyY: 0.3, rTop: 0.036, rFoot: 0.032,
    color: c.leg, hoof: { color: c.nail, r: 0.033, h: 0.02 },
  });
  parts.push(
    // massive barrel, shoulder hump and heavy rump
    { geometry: new THREE.IcosahedronGeometry(0.17, 0), color: c.hide,
      position: [-0.02, 0.3, 0], scale: [1.55, 1.02, 1.12] },
    { geometry: new THREE.IcosahedronGeometry(0.1, 0), color: c.shade,
      position: [0.1, 0.355, 0], scale: [1.0, 0.92, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.115, 0), color: c.shade,
      position: [-0.17, 0.315, 0], scale: [1.0, 0.95, 1.0] },
    // domed head
    { geometry: new THREE.IcosahedronGeometry(0.105, 0), color: c.hide,
      position: [0.27, 0.34, 0], scale: [1.05, 1.1, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.055, 0), color: c.hide,
      position: [0.285, 0.41, 0], scale: [1.0, 0.85, 0.9] },
    // four-segment trunk curling down, the tip lifted forward
    safari.tube([0.355, 0.32, 0], [0.41, 0.245, 0], 0.034, 0.028, c.hide, 5),
    safari.tube([0.41, 0.245, 0], [0.435, 0.16, 0], 0.028, 0.022, c.hide, 5),
    safari.tube([0.435, 0.16, 0], [0.435, 0.085, 0.008], 0.022, 0.017, c.hide, 5),
    safari.tube([0.435, 0.085, 0.008], [0.452, 0.045, 0.012], 0.017, 0.011, c.hide, 5),
    // rope tail with a dark tuft
    safari.tube([-0.27, 0.3, 0], [-0.305, 0.16, 0.006], 0.011, 0.005, c.shade),
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.tuft,
      position: [-0.308, 0.148, 0.007] },
  );
  // huge two-layer fan ears (dark outer panel, dusty-pink inner lining)
  // and long curved tusks, mirrored per side.
  for (const s of [1, -1] as const) {
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.105, 0), color: c.shade,
        position: [0.235, 0.36, s * 0.115], rotation: [0, -s * 0.4, 0],
        scale: [0.32, 1.3, 1.2] },
      { geometry: new THREE.IcosahedronGeometry(0.105, 0), color: c.earIn,
        position: [0.248, 0.36, s * 0.108], rotation: [0, -s * 0.4, 0],
        scale: [0.16, 1.05, 0.95] },
      safari.tube([0.33, 0.26, s * 0.06], [0.4, 0.215, s * 0.075], 0.012, 0.009, c.tusk),
      safari.tube([0.4, 0.215, s * 0.075], [0.455, 0.225, s * 0.082], 0.009, 0.004, c.tusk),
    );
  }
  parts.push(...safari.eyes(0.345, 0.365, 0.082, 0.0065, "#241e18"));
  return parts;
}

export const elephantV2 = defineModel({
  id: "elephant-v2",
  scale: 2.63, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Elephant (v2)",
  tags: ["creature","elephant","low-poly","age-of-mcp","fable"],
  summary: "Low-poly elephant.",
  front: "+x",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.3,
  fits: ["draft"],
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => elephantParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => elephantParts(1) },
  ],
  animations: [],
});
